// ==================== STATISTICS SERVICE ====================

import axios from 'axios';
import { APP_CONFIG } from '../utils/constants';

// Create axios instance for statistics
const statsAPI = axios.create({
  baseURL: APP_CONFIG.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: APP_CONFIG.REQUEST_TIMEOUT,
});

// Add token to requests automatically
statsAPI.interceptors.request.use(
  config => {
    const token = localStorage.getItem('authToken');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

export const statisticsService = {
  // Get dashboard statistics
  // NOTE: This method loads a limited set of certificates for dashboard stats.
  // For better performance, consider using a dedicated backend statistics API endpoint.
  getDashboardStats: async () => {
    try {
      // Fetch recent certificates (limited to 200) and users data in parallel
      const [certificatesResponse, usersResponse] = await Promise.all([
        statsAPI.get('/api/Certificates', {
          params: { page: 1, pageSize: 200 }, // Reduced from 1000 to 200 for better performance
        }),
        statsAPI.get('/api/Auth/users').catch(() => ({ data: [] })), // Fallback for non-admin users
      ]);

      const certificates = certificatesResponse.data || [];
      const users = usersResponse.data || [];

      // Calculate statistics - use header for total count
      const totalCertificates = parseInt(certificatesResponse.headers['x-total-count']) || certificates.length;
      const activeUsers = users.filter(user => user.isActive !== false).length;

      // Calculate dates for time-based statistics
      const now = new Date();
      const lastMonth = new Date(
        now.getFullYear(),
        now.getMonth() - 1,
        now.getDate()
      );
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Count certificates created this month and last month
      const thisMonthCerts = certificates.filter(
        cert => new Date(cert.CreatedAt || cert.IssuanceDate) >= thisMonth
      ).length;

      const lastMonthCerts = certificates.filter(cert => {
        const certDate = new Date(cert.CreatedAt || cert.IssuanceDate);
        return certDate >= lastMonth && certDate < thisMonth;
      }).length;

      // Count pending certificates (you might need to adjust this based on your certificate status field)
      const pendingCertificates = certificates.filter(
        cert =>
          cert.Status === 'pending' || cert.Status === 'review' || !cert.Status
      ).length;

      // Calculate growth percentages
      const certificateGrowth =
        lastMonthCerts > 0
          ? (
              ((thisMonthCerts - lastMonthCerts) / lastMonthCerts) *
              100
            ).toFixed(1)
          : thisMonthCerts > 0
            ? 100
            : 0;

      // Count new users this month
      const thisMonthUsers = users.filter(
        user => new Date(user.CreatedAt || user.RegistrationDate) >= thisMonth
      ).length;

      const lastMonthUsers = users.filter(user => {
        const userDate = new Date(user.CreatedAt || user.RegistrationDate);
        return userDate >= lastMonth && userDate < thisMonth;
      }).length;

      const userGrowth =
        lastMonthUsers > 0
          ? (
              ((thisMonthUsers - lastMonthUsers) / lastMonthUsers) *
              100
            ).toFixed(1)
          : thisMonthUsers > 0
            ? 100
            : 0;

      return {
        totalCertificates,
        activeUsers,
        pendingCertificates,
        monthlyGrowth: certificateGrowth,
        userGrowth,
        certificates,
        users,
      };
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      throw error;
    }
  },

  // Get recent activities based on certificates and users data
  getRecentActivities: async () => {
    try {
      const [certificatesResponse, usersResponse] = await Promise.all([
        statsAPI.get('/api/Certificates', {
          params: { page: 1, pageSize: 20 },
        }),
        statsAPI.get('/api/Auth/users').catch(() => ({ data: [] })),
      ]);

      const certificates = certificatesResponse.data || [];
      const users = usersResponse.data || [];

      const activities = [];

      // Add certificate activities
      certificates
        .sort(
          (a, b) =>
            new Date(b.CreatedAt || b.IssuanceDate) -
            new Date(a.CreatedAt || a.IssuanceDate)
        )
        .slice(0, 10)
        .forEach(cert => {
          activities.push({
            id: `cert-${cert.Id}`,
            title: `Certificate ${cert.CocNumber || cert.Id} created`,
            description: `${cert.ImporterName || 'Certificate'} - ${cert.ItemDescription || 'Import certificate'}`,
            time: formatTimeAgo(cert.CreatedAt || cert.IssuanceDate),
            type: 'create',
            timestamp: new Date(cert.CreatedAt || cert.IssuanceDate),
          });
        });

      // Add user activities (if admin has access)
      users
        .sort(
          (a, b) =>
            new Date(b.CreatedAt || b.RegistrationDate) -
            new Date(a.CreatedAt || a.RegistrationDate)
        )
        .slice(0, 5)
        .forEach(user => {
          activities.push({
            id: `user-${user.id}`,
            title: `User ${user.FirstName} ${user.LastName} registered`,
            description: `${user.Roles || 'Office'} user account created`,
            time: formatTimeAgo(user.CreatedAt || user.RegistrationDate),
            type: 'user',
            timestamp: new Date(user.CreatedAt || user.RegistrationDate),
          });
        });

      // Sort all activities by timestamp and return top 8
      return activities.sort((a, b) => b.timestamp - a.timestamp).slice(0, 8);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      return [];
    }
  },
};

// Helper function to format time ago
function formatTimeAgo(dateString) {
  if (!dateString) return 'Unknown time';

  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600)
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400)
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000)
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000)
    return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
}
